'use client';

import { useState } from 'react';

const ImageUpload = ({ onImageUpload, currentImage, label = 'Upload Image' }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFileUpload = async file => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 10MB to match backend)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      alert('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || data.message || 'Upload failed');
      }

      const data = await response.json();
      
      if (data.url) {
        onImageUpload(data.url);
      } else {
        throw new Error('No image URL received from server');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const handleDragOver = e => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    setDragOver(false);
  };

  return (
    <div className="mb-3">
      <label className="form-label fw-bold">
        <i className="fas fa-image me-2"></i>
        {label}
      </label>

      {/* Current Image Preview */}
      {currentImage && (
        <div className="mb-3">
          <div className="card" style={{ maxWidth: '300px' }}>
            <img
              src={currentImage || '/placeholder.svg'}
              alt="Current"
              className="card-img-top"
              style={{ height: '150px', objectFit: 'cover' }}
            />
            <div className="card-body p-2">
              <small className="text-muted">Current image</small>
            </div>
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`border-2 border-dashed rounded p-4 text-center ${
          dragOver ? 'border-primary bg-light' : 'border-secondary'
        }`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={{ cursor: 'pointer' }}
      >
        {uploading ? (
          <div>
            <div className="spinner-border text-primary mb-2"></div>
            <p className="mb-0">Uploading...</p>
          </div>
        ) : (
          <div>
            <i
              className="fas fa-cloud-upload-alt mb-2"
              style={{ fontSize: '2rem', color: '#6c757d' }}
            ></i>
            <p className="mb-2">Drag & drop an image here, or click to select</p>
            <input
              type="file"
              className="form-control"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id={`file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`}
            />
            <label
              htmlFor={`file-upload-${label.replace(/\s+/g, '-').toLowerCase()}`}
              className="btn btn-outline-primary"
            >
              <i className="fas fa-folder-open me-2"></i>
              Choose File
            </label>
            <div className="mt-2">
              <small className="text-muted">Supported formats: JPG, PNG, GIF (Max 10MB)</small>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
